import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, AlertChannel } from "@/lib/api";
import { getToken } from "../lib/authToken";
import { CloudNavBar } from "../components/CloudNavBar";
import { ChevronDown, ChevronRight } from "@ensafely/checker-ui";

interface EdgeAlertConfig {
  enabled: boolean;
  alert_channel: string;
}

interface EdgeInstance {
  id: string;
  region: string;
  status: "connected" | "stale" | "disconnected";
  version: string | null;
  last_heartbeat_at: string | null;
  remote_addr: string | null;
  created_at: string;
}

interface EdgeInstancesResponse {
  edge_instances: EdgeInstance[];
  quota: {
    current: number;
    limit: number;
  };
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "offline";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "just now";
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function StatusBadge({ status }: { status: EdgeInstance["status"] }) {
  const styles = {
    connected: "bg-green-100 text-green-800 border-green-200",
    stale: "bg-yellow-100 text-yellow-800 border-yellow-200",
    disconnected: "bg-gray-100 text-gray-600 border-gray-200",
  };
  const dots = {
    connected: "bg-green-500",
    stale: "bg-yellow-500",
    disconnected: "bg-gray-400",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dots[status]}`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function SetupInstructionsCard() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-card rounded-lg shadow mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/30 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-base font-semibold">Deploy a Probe</span>
        </div>
        <span className="text-xs text-muted-foreground">{expanded ? "Collapse" : "Expand"}</span>
      </button>

      {expanded && (
        <div className="px-6 pb-6 space-y-4 border-t">
          <p className="text-sm text-muted-foreground pt-4">
            Deploy the edge probe binary or Docker container at your location. You'll need an API
            key to authenticate — create one on the{" "}
            <Link to="/account/api-keys" className="text-primary underline hover:no-underline">
              API Keys page
            </Link>
            .
          </p>
          <p className="text-sm text-muted-foreground">
            For detailed setup instructions including Docker, Binary, Helm, and Kubernetes
            deployment options, see our documentation:
          </p>
          <a
            href="https://ensafely.com/docs#on-premises"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary underline hover:no-underline font-medium"
          >
            → On-Premises Probe Setup Guide
          </a>
        </div>
      )}
    </div>
  );
}

function EdgeAlertConfigCard() {
  const [config, setConfig] = useState<EdgeAlertConfig>({ enabled: false, alert_channel: "" });
  const [channels, setChannels] = useState<AlertChannel[]>([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    Promise.all([
      api.get<EdgeAlertConfig>("/settings/edge-alerts"),
      api.getAlertChannels(),
    ])
      .then(([cfg, chs]) => {
        setConfig(cfg);
        setChannels(chs);
      })
      .catch((e) => setError(e?.message || "Failed to load edge alert config"))
      .finally(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const saved = await api.put<EdgeAlertConfig>("/settings/edge-alerts", config);
      setConfig(saved);
      setSuccess("Edge alert settings saved");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e: any) {
      setError(e?.message || "Failed to save edge alert config");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="bg-card rounded-lg shadow mb-6">
      <div className="px-6 py-4 border-b">
        <h2 className="text-base font-semibold">Edge Health Alerts</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Get notified when your edge probes disconnect or go stale.
        </p>
      </div>
      <div className="px-6 py-4 space-y-4">
        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && <p className="text-sm text-green-600">{success}</p>}

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-sm font-medium">
            Alert me when edge probes disconnect or go stale
          </span>
        </label>

        {config.enabled && (
          <div>
            {channels.length === 0 ? (
              <>
                <span className="block text-sm font-medium mb-1">Alert channel</span>
                <p className="text-sm text-muted-foreground">
                  No alert channels configured.{" "}
                  <Link to="/settings" className="text-primary underline hover:no-underline">
                    Create one in Settings
                  </Link>{" "}
                  first.
                </p>
              </>
            ) : (
              <label className="block">
                <span className="block text-sm font-medium mb-1">Alert channel</span>
                <select
                  value={config.alert_channel}
                  onChange={(e) => setConfig({ ...config, alert_channel: e.target.value })}
                  className="w-full max-w-xs rounded border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select a channel...</option>
                  {channels.map((ch) => (
                    <option key={ch.name} value={ch.name}>
                      {ch.name} ({ch.type})
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        <div>
          <button
            onClick={handleSave}
            disabled={saving || (config.enabled && !config.alert_channel)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UpgradeCTA() {
  return (
    <div className="bg-card rounded-lg shadow p-8 text-center">
      <div className="text-4xl mb-4">📡</div>
      <h2 className="text-lg font-semibold mb-2">Edge Probes not available on your plan</h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
        Edge probes let you run health checks from your own infrastructure and report results to
        the platform. Upgrade your plan to deploy edge probes in your private network or on-prem
        locations.
      </p>
      <Link
        to="/account/billing"
        className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:opacity-90"
      >
        Upgrade Plan
      </Link>
    </div>
  );
}

export default function EdgeProbesPage() {
  const [instances, setInstances] = useState<EdgeInstance[]>([]);
  const [quota, setQuota] = useState<{ used: number; limit: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchInstances = useCallback(() => {
    return api
      .get<EdgeInstancesResponse>("/edge-instances")
      .then((res) => {
        setInstances(res.edge_instances ?? []);
        setQuota(res.quota ? { used: res.quota.current, limit: res.quota.limit } : null);
      })
      .catch((e) => setError(e?.message || "Failed to load edge probes"));
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchInstances().finally(() => setLoading(false));
  }, [fetchInstances]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      fetchInstances();
    }, 30_000);
    return () => clearInterval(timer);
  }, [fetchInstances]);

  // WebSocket real-time updates
  useEffect(() => {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const token = getToken();
    if (!token) return;

    const url = `${proto}//${window.location.host}/ws?token=${encodeURIComponent(token)}`;
    let ws: WebSocket | null = null;
    let shouldReconnect = true;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "edge_connected") {
            setInstances((prev) => {
              const exists = prev.find((i) => i.id === msg.instance?.id);
              if (exists) {
                return prev.map((i) =>
                  i.id === msg.instance?.id ? { ...i, ...msg.instance, status: "connected" } : i
                );
              }
              return msg.instance ? [msg.instance, ...prev] : prev;
            });
            if (msg.instance) {
              setQuota((q) => (q ? { ...q, used: q.used + 1 } : q));
            }
          } else if (msg.type === "edge_disconnected") {
            setInstances((prev) =>
              prev.map((i) =>
                i.id === msg.instance_id ? { ...i, status: "disconnected" } : i
              )
            );
          } else if (msg.type === "edge_stale") {
            setInstances((prev) =>
              prev.map((i) =>
                i.id === msg.instance_id ? { ...i, status: "stale" } : i
              )
            );
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        scheduleReconnect();
      };

      ws.onerror = () => {
        ws?.close();
      };
    }

    function scheduleReconnect() {
      if (!shouldReconnect) return;
      reconnectTimer = setTimeout(connect, 5000);
    }

    connect();

    return () => {
      shouldReconnect = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, []);

  const handleDelete = async (id: string, region: string) => {
    if (!confirm(`Delete edge probe "${region}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await api.del(`/edge-instances/${id}`);
      setInstances((prev) => prev.filter((i) => i.id !== id));
      setQuota((q) => (q ? { ...q, used: Math.max(0, q.used - 1) } : q));
    } catch (e: any) {
      alert(e?.message || "Failed to delete edge probe");
    } finally {
      setDeletingId(null);
    }
  };

  // Determine if plan allows edge probes (limit 0 = not allowed)
  const planAllowsEdges = !quota || quota.limit !== 0;

  return (
    <div className="min-h-screen bg-background">
      <CloudNavBar />
      <div className="max-w-5xl mx-auto py-8 px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold">Edge Probes</h1>
          {quota && planAllowsEdges && (
            <span className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded-full">
              {quota.used} / {quota.limit < 0 ? "Unlimited" : quota.limit} edge probes used
            </span>
          )}
        </div>

        <p className="text-xs text-muted-foreground mb-6">
          Edge probes authenticate using API keys.{" "}
          <Link to="/account/api-keys" className="underline hover:no-underline">
            Manage API Keys &rarr;
          </Link>
        </p>

        {!loading && !planAllowsEdges ? (
          <UpgradeCTA />
        ) : (
          <>
            {/* Info banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6 text-sm text-blue-800">
              Edge probes run health checks from your own infrastructure and report results to the
              platform. Create an API key, then deploy the probe with the setup instructions below.
            </div>

            {/* Setup Instructions */}
            <SetupInstructionsCard />

            {/* Edge Health Alert Config */}
            <EdgeAlertConfigCard />

            {/* Edge Probes — grouped by region */}
            <div className="bg-card rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b">
                <h2 className="text-base font-semibold">Connected Probes</h2>
              </div>

              {error && <p className="text-sm text-red-600 p-4">{error}</p>}

              {loading ? (
                <p className="text-sm text-muted-foreground p-6">Loading...</p>
              ) : instances.length === 0 ? (
                <p className="text-sm text-muted-foreground p-6">
                  No edge probes connected. Follow the setup instructions above to deploy your
                  first probe.
                </p>
              ) : (
                (() => {
                  // Group instances by region, preserving order of first appearance.
                  const regionOrder: string[] = [];
                  const grouped: Record<string, EdgeInstance[]> = {};
                  for (const inst of instances) {
                    if (!grouped[inst.region]) {
                      grouped[inst.region] = [];
                      regionOrder.push(inst.region);
                    }
                    grouped[inst.region].push(inst);
                  }
                  return (
                    <div className="divide-y">
                      {regionOrder.map((region) => {
                        const probes = grouped[region];
                        const connectedCount = probes.filter((p) => p.status === "connected").length;
                        return (
                          <div key={region}>
                            {/* Region header — shown when multiple probes exist */}
                            {probes.length > 1 && (
                              <div className="px-6 py-2 bg-muted/30 flex items-center gap-2">
                                <span className="text-sm font-semibold">{region}</span>
                                <span className="text-xs text-muted-foreground">
                                  {connectedCount}/{probes.length} connected
                                </span>
                              </div>
                            )}
                            <table className="w-full text-sm">
                              <thead className={probes.length > 1 ? "sr-only" : "bg-muted/50"}>
                                <tr>
                                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">
                                    Region
                                  </th>
                                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">
                                    Status
                                  </th>
                                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">
                                    Version
                                  </th>
                                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">
                                    Last Heartbeat
                                  </th>
                                  <th className="text-left px-6 py-3 font-medium text-muted-foreground">
                                    IP
                                  </th>
                                  <th className="px-6 py-3" />
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {probes.map((instance, idx) => (
                                  <tr
                                    key={instance.id}
                                    className="hover:bg-muted/30 transition-colors"
                                  >
                                    <td className="px-6 py-3 font-medium">
                                      {probes.length > 1
                                        ? `${instance.region} #${idx + 1}`
                                        : instance.region}
                                    </td>
                                    <td className="px-6 py-3">
                                      <StatusBadge status={instance.status} />
                                    </td>
                                    <td className="px-6 py-3 text-muted-foreground font-mono text-xs">
                                      {instance.version ?? "\u2014"}
                                    </td>
                                    <td className="px-6 py-3 text-muted-foreground">
                                      {formatRelativeTime(instance.last_heartbeat_at)}
                                    </td>
                                    <td className="px-6 py-3 text-muted-foreground font-mono text-xs">
                                      {instance.remote_addr ?? "\u2014"}
                                    </td>
                                    <td className="px-6 py-3 text-right">
                                      <button
                                        onClick={() => handleDelete(instance.id, instance.region)}
                                        disabled={deletingId === instance.id}
                                        className="text-red-600 hover:underline text-xs disabled:opacity-50"
                                      >
                                        {deletingId === instance.id ? "Deleting..." : "Delete"}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
